import React, { useMemo, useCallback } from 'react';
import queryString from 'qs';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';

import { notImplemented } from '@/utils/functions';

const PAGE_SIZE = 10;
const PAGE_INDEX = 1;
const positionApplicantsDefaultSearch = {
  digestkey: '',
  stageId: undefined,
  page: PAGE_INDEX,
  perPage: PAGE_SIZE,
  status: ['in_progress', 'hired'],
  updateTimestampe: new Date().getTime(),
  sort_key: 'days_in_current_stage',
  order: 'ascend',
  tag_ids: [],
  rating_gte: '',
  filters: {},
};
type ApplicantsPageSearch = typeof positionApplicantsDefaultSearch | queryString.ParsedQs | { digestkey?: string };

export interface URLSearchContextProps {
  applicantsPageSearch: ApplicantsPageSearch;
  navigateWithSearch: (newPageSearch: Record<string, string>) => void;
}

interface URLSearchProviderProps {
  children: React.ReactNode;
}

const URLSearchContext = React.createContext<URLSearchContextProps>({
  applicantsPageSearch: {} as ApplicantsPageSearch,
  navigateWithSearch: notImplemented,
});

function getQueryObject(search: string) {
  return queryString.parse(search, {
    ignoreQueryPrefix: true,
    arrayLimit: 999999,
  });
}

function URLSearchProvider({ children }: URLSearchProviderProps) {
  const location = useLocation();
  const history = useHistory();

  const queryObject = useMemo(() => {
    return getQueryObject(location.search);
  }, [location]);

  // url search for position applicants page.
  // the `applicantsPageSearch` is page specific.
  // TODO: Demo will figure out a way to enable putting this logic into specific page, like the redux connect
  // positions applicants page URL pattern:
  // <Route exact path="/applicants/:location/:position/:digestkey" component={Applicants} />
  const match = useRouteMatch<{ digestkey: string }>('/applicants/:location/:position/:digestkey');
  const digestkey = match?.params?.digestkey;

  const applicantsPageSearch = useMemo(() => {
    return {
      ...positionApplicantsDefaultSearch,
      ...queryObject,
      stageId: queryObject.stageId ? Number(queryObject.stageId) : undefined,
      digestkey,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, digestkey, queryObject]);

  // global function to update the url with new search
  const navigateWithSearch = useCallback(
    (newPageSearch: unknown) => {
      history.push({
        pathname: location.pathname,
        search: `?${queryString.stringify(newPageSearch)}`,
      });
    },
    [location, history],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contextValue = useMemo(() => ({ applicantsPageSearch, navigateWithSearch }), [applicantsPageSearch, location]);

  return <URLSearchContext.Provider value={contextValue}>{children}</URLSearchContext.Provider>;
}

function withURLSearchContext(Component: React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>) {
  return function WithURLSearchContext(props: Record<string, unknown>) {
    const context = React.useContext(URLSearchContext);

    if (context == null) {
      throw new Error('withURLSearchContext must be used within a URLSearchProvider');
    }

    return <Component {...props} urlSearchContext={context} />;
  };
}

export { URLSearchProvider, withURLSearchContext };
